/**
 * LoginGate - Password gate for SENTINEL C2 Edge Intelligence Platform
 *
 * Simple access control with session storage persistence.
 * Multiple access codes supported (all unlimited use).
 */

import { useState, useEffect, useCallback } from 'react'

const VALID_PASSWORDS = new Set([
  '20Mtoday',
  'Trash2026',
  'C2edge',
  'C2edge2',
  'C2edge26',
])
const SESSION_KEY = 'sentinel-c2-auth-session'

interface LoginGateProps {
  children: React.ReactNode
}

export function LoginGate({ children }: LoginGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const session = sessionStorage.getItem(SESSION_KEY)
    if (session === 'authenticated') {
      setIsAuthenticated(true)
    }
    setIsChecking(false)
  }, [])

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setError('')

      if (VALID_PASSWORDS.has(password)) {
        sessionStorage.setItem(SESSION_KEY, 'authenticated')
        setIsAuthenticated(true)
      } else {
        setError('ACCESS DENIED — Invalid credentials')
        setPassword('')
      }
    },
    [password]
  )

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY)
    setIsAuthenticated(false)
    setPassword('')
  }, [])

  if (isChecking) {
    return (
      <div className="min-h-screen bg-term-bgDark flex items-center justify-center">
        <div className="text-term-textDim font-mono">Initializing secure session...</div>
      </div>
    )
  }

  if (isAuthenticated) {
    return (
      <>
        {children}
        <button
          onClick={handleLogout}
          className="fixed bottom-4 right-4 px-3 py-1 text-xs bg-term-bgLight text-term-textDim hover:text-term-text border border-term-border rounded transition-colors z-50 font-mono"
          title="Logout"
        >
          [logout]
        </button>
      </>
    )
  }

  return (
    <div className="min-h-screen bg-term-bgDark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Terminal-style header */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <pre className="text-term-primary text-xs leading-tight inline-block text-left">
{` ___  ___ _  _ _____ ___ _  _ ___ _
/ __|/ __| \\| |_   _|_ _| \\| | __| |
\\__ \\  _|| .\` | | |  | || .\` | _|| |__
|___/___|_|\\_| |_| |___|_|\\_|___|____|`}
            </pre>
          </div>
          <h1 className="text-xl font-bold text-term-text mb-1">
            C2 Edge Intelligence Platform
          </h1>
          <p className="text-term-textDim text-sm font-mono">
            W4M.ai // Air Force Command &amp; Control
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="term-box p-6">
            <div className="flex items-center gap-2 mb-4 text-term-textDim text-xs">
              <span className="text-term-success">$</span>
              <span>authenticate --clearance=UNCLASSIFIED --session=secure</span>
            </div>

            <label className="block text-term-textDim text-sm mb-2 font-mono">
              Access Code
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="term-input"
              placeholder="Enter access code..."
              autoFocus
            />

            {error && (
              <p className="mt-3 text-term-error text-sm font-mono">
                <span className="text-term-error mr-1">!</span>
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-term-primary text-white font-medium rounded hover:bg-term-primaryBright transition-colors font-mono"
          >
            [ AUTHENTICATE ]
          </button>
        </form>

        {/* Footer */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-term-textDim text-xs font-mono">
            UNCLASSIFIED // DEMO ENVIRONMENT // SYNTHETIC DATA ONLY
          </p>
          <p className="text-term-border text-xs font-mono">
            v0.1.0 // SENTINEL C2 Edge Intelligence
          </p>
        </div>
      </div>
    </div>
  )
}
